import { useEffect } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import { getSupabaseClient, hasSupabaseConfig } from '@/lib/supabaseClient'

export function isRealtimeEnabled(): boolean {
  return hasSupabaseConfig()
}

function usePostgresInvalidation(
  channelName: string,
  table: string,
  filter: string | undefined,
  onChange: () => void,
  enabled: boolean,
): void {
  useEffect(() => {
    if (!enabled || !isRealtimeEnabled()) return

    const channel = getSupabaseClient()
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table,
          ...(filter ? { filter } : {}),
        },
        onChange,
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          onChange()
        }
      })

    return () => {
      void getSupabaseClient().removeChannel(channel)
    }
  }, [channelName, enabled, filter, onChange, table])
}

export function useDropPhotosSubscription(
  dropId: string | undefined,
  queryClient: QueryClient,
): void {
  usePostgresInvalidation(
    `drop:${dropId}:photos`,
    'photos',
    dropId ? `drop_id=eq.${dropId}` : undefined,
    () => {
      void queryClient.invalidateQueries({ queryKey: ['drop-photos', dropId] })
      void queryClient.invalidateQueries({ queryKey: ['drop', dropId] })
    },
    !!dropId,
  )
}

export function useDropSubscription(
  id: string | undefined,
  queryClient: QueryClient,
  _onDeleted?: () => void,
): void {
  usePostgresInvalidation(
    `drop:${id}:status`,
    'drops',
    id ? `id=eq.${id}` : undefined,
    () => {
      void queryClient.invalidateQueries({ queryKey: ['drop', id] })
      void queryClient.invalidateQueries({ queryKey: ['drops'] })
    },
    !!id,
  )
}

export function useDropActivitySubscription(
  dropId: string | undefined,
  queryClient: QueryClient,
): void {
  usePostgresInvalidation(
    `drop:${dropId}:activity`,
    'activity_log',
    dropId ? `drop_id=eq.${dropId}` : undefined,
    () => {
      void queryClient.invalidateQueries({ queryKey: ['activity', dropId] })
    },
    !!dropId,
  )
}
